import React, { useState, useEffect, useCallback } from 'react';
import ConfirmationModal from '../../ui/ConfirmationModal';
import {
  MessageSquare,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Flame,
  ArrowUpDown,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Eye,
  Image as ImageIcon,
  FileText,
  X,
  Loader2,
  Trash2,
} from 'lucide-react';
import {
  fetchAdminFeedbackList,
  fetchAdminFeedbackDetail,
  updateAdminFeedback,
  deleteAdminFeedback,
  type FeedbackItem,
  type FeedbackStatus,
  type FeedbackPriority,
  type FeedbackCategory,
  type FeedbackSource,
} from '../../../lib/feedbackApi';

interface BetaFeedbackSectionProps {
  theme?: 'light' | 'dark';
}

const STATUS_BADGES: Record<FeedbackStatus, { label: string; bg: string; text: string }> = {
  NEW: { label: 'New', bg: 'bg-blue-500/10 border-blue-500/20', text: 'text-blue-600 dark:text-blue-400' },
  REVIEWING: { label: 'Reviewing', bg: 'bg-amber-500/10 border-amber-500/20', text: 'text-amber-600 dark:text-amber-400' },
  FIXED: { label: 'Fixed', bg: 'bg-emerald-500/10 border-emerald-500/20', text: 'text-emerald-600 dark:text-emerald-400' },
  CLOSED: { label: 'Closed', bg: 'bg-zinc-500/10 border-zinc-500/20', text: 'text-zinc-500 dark:text-zinc-400' },
};

const PRIORITY_BADGES: Record<FeedbackPriority, { label: string; text: string }> = {
  LOW: { label: 'Low', text: 'text-zinc-400' },
  MEDIUM: { label: 'Medium', text: 'text-blue-400' },
  HIGH: { label: 'High', text: 'text-amber-500 font-semibold' },
  CRITICAL: { label: 'Critical', text: 'text-red-500 font-bold' },
};

export const BetaFeedbackSection: React.FC<BetaFeedbackSectionProps> = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [metrics, setMetrics] = useState({ total: 0, new: 0, reviewing: 0, fixed: 0, closed: 0, critical: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<FeedbackSource | ''>('');
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [adminNotesDraft, setAdminNotesDraft] = useState('');
  const [updating, setUpdating] = useState(false);
  const [activeScreenshotPreview, setActiveScreenshotPreview] = useState<{ url: string; alt: string } | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAdminFeedbackList({
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        category: categoryFilter || undefined,
        source: sourceFilter || undefined,
        search: search || undefined,
        page,
        pageSize: 15,
      });
      setItems(Array.isArray(res?.items) ? res.items : []);
      if (res?.metrics) setMetrics(res.metrics);
      setTotalPages(res?.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Failed to load feedback list:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, categoryFilter, sourceFilter, search, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const visibleIds = new Set(items.map((item) => item.id));
    setSelectedIds((current) => current.filter((id) => visibleIds.has(id)));
  }, [items]);

  const toggleFeedbackSelection = (id: string) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]);
  };

  const togglePageSelection = () => {
    const pageIds = items.map((item) => item.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    setSelectedIds((current) => allPageSelected
      ? current.filter((id) => !pageIds.includes(id))
      : [...new Set([...current, ...pageIds])]);
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length) setDeleteConfirmationOpen(true);
  };

  const confirmDeleteSelected = async () => {
    if (!selectedIds.length || deleting) return;
    setDeleting(true);
    try {
      await deleteAdminFeedback(selectedIds);
      if (selectedFeedback && selectedIds.includes(selectedFeedback.id)) setSelectedFeedback(null);
      setSelectedIds([]);
      setDeleteConfirmationOpen(false);
      await loadData();
    } catch (err) {
      console.error('Failed to delete selected feedback:', err);
      window.alert(err instanceof Error ? err.message : 'Failed to delete selected feedback.');
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const detail = await fetchAdminFeedbackDetail(id);
      setSelectedFeedback(detail);
      setAdminNotesDraft(detail.adminNotes || '');
    } catch (err) {
      console.error('Failed to fetch feedback details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: FeedbackStatus) => {
    if (!selectedFeedback) return;
    setUpdating(true);
    try {
      const updated = await updateAdminFeedback(selectedFeedback.id, { status: newStatus });
      setSelectedFeedback(updated);
      loadData();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handlePriorityChange = async (newPriority: FeedbackPriority) => {
    if (!selectedFeedback) return;
    setUpdating(true);
    try {
      const updated = await updateAdminFeedback(selectedFeedback.id, { priority: newPriority });
      setSelectedFeedback(updated);
      loadData();
    } catch (err) {
      console.error('Failed to update priority:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedFeedback) return;
    setUpdating(true);
    try {
      await updateAdminFeedback(selectedFeedback.id, { adminNotes: adminNotesDraft });
      setSelectedFeedback(null);
    } catch (err) {
      console.error('Failed to save admin notes:', err);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. Header & Quick Metrics */}
      <div className={`-mx-6 -mt-6 mb-6 flex h-[84.8px] flex-col items-start justify-between gap-4 px-4 py-0 pt-[10px] sm:flex-row sm:items-center ${isDark ? 'border-b-[0.8px] border-[rgba(30,41,59,0.7)] bg-gradient-to-r from-slate-800/70 to-slate-900/70 backdrop-blur-md' : 'border-b border-zinc-200 bg-zinc-50'}`}>
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
            <MessageSquare className="h-4 w-4 text-cyan-500 dark:text-cyan-400" />
            <span>USER FEEDBACK</span>
          </h2>
          <p className="mt-0 pb-[10px] text-sm font-normal text-zinc-500 dark:text-slate-400">
            Review, triage, and resolve incoming feedback from beta testers and customers.
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="inline-flex h-10 items-center justify-center gap-2 self-start rounded-lg border border-zinc-300 bg-white px-5 py-2.5 text-sm font-medium text-zinc-800 shadow-sm transition-colors hover:border-zinc-400 hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white sm:self-auto dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:hover:border-slate-600 dark:hover:bg-slate-700 dark:focus:ring-offset-slate-900"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white dark:bg-zinc-900/60 p-3 rounded-xl border border-zinc-200 dark:border-zinc-800">
          <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider block">Total Reports</span>
          <span className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mt-1 block">{metrics?.total ?? 0}</span>
        </div>
        <div className="bg-blue-500/5 dark:bg-blue-500/10 p-3 rounded-xl border border-blue-500/20">
          <span className="text-[11px] font-semibold text-blue-500 uppercase tracking-wider block">New</span>
          <span className="text-xl font-bold text-blue-600 dark:text-blue-400 mt-1 block">{metrics?.new ?? 0}</span>
        </div>
        <div className="bg-amber-500/5 dark:bg-amber-500/10 p-3 rounded-xl border border-amber-500/20">
          <span className="text-[11px] font-semibold text-amber-500 uppercase tracking-wider block">Reviewing</span>
          <span className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-1 block">{metrics?.reviewing ?? 0}</span>
        </div>
        <div className="bg-emerald-500/5 dark:bg-emerald-500/10 p-3 rounded-xl border border-emerald-500/20">
          <span className="text-[11px] font-semibold text-emerald-500 uppercase tracking-wider block">Fixed</span>
          <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 block">{metrics?.fixed ?? 0}</span>
        </div>
        <div className="bg-zinc-500/5 dark:bg-zinc-500/10 p-3 rounded-xl border border-zinc-500/20">
          <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider block">Closed</span>
          <span className="text-xl font-bold text-zinc-600 dark:text-zinc-400 mt-1 block">{metrics?.closed ?? 0}</span>
        </div>
        <div className="bg-red-500/5 dark:bg-red-500/10 p-3 rounded-xl border border-red-500/20">
          <span className="text-[11px] font-semibold text-red-500 uppercase tracking-wider block flex items-center gap-1">
            <Flame className="h-3 w-3" /> Critical
          </span>
          <span className="text-xl font-bold text-red-600 dark:text-red-400 mt-1 block">{metrics?.critical ?? 0}</span>
        </div>
      </div>

      {/* 2. Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by description, tester, or route..."
            className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value as FeedbackSource | ''); setPage(1); }}
            aria-label="Filter feedback source"
            className="text-xs rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-zinc-700 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Sources</option>
            <option value="BETA_TESTER">Beta Tester</option>
            <option value="REGULAR_CUSTOMER">Regular Customer</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-zinc-700 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Statuses</option>
            <option value="NEW">New</option>
            <option value="REVIEWING">Reviewing</option>
            <option value="FIXED">Fixed</option>
            <option value="CLOSED">Closed</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="text-xs rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-zinc-700 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Priorities</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="CRITICAL">Critical</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-xs rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-zinc-700 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Categories</option>
            <option value="BUG_REPORT">Bug Report</option>
            <option value="INCORRECT_DATA">Incorrect Data</option>
            <option value="USABILITY_PROBLEM">Usability Problem</option>
            <option value="FEATURE_SUGGESTION">Feature Suggestion</option>
            <option value="PERFORMANCE_ISSUE">Performance Issue</option>
            <option value="GENERAL_FEEDBACK">General Feedback</option>
          </select>
        </div>
      </div>

      {/* 3. Feedback Table */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden bg-white dark:bg-zinc-900/40">
        <div className="flex min-h-11 items-center justify-between gap-3 border-b border-zinc-200 px-4 py-2 dark:border-zinc-800">
          <span className="text-xs text-zinc-500 dark:text-zinc-400" aria-live="polite">
            {selectedIds.length ? `${selectedIds.length} selected` : 'Select feedback reports to manage them'}
          </span>
          <button type="button" onClick={handleDeleteSelected} disabled={!selectedIds.length || deleting} className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-40 dark:text-red-400">
            {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Delete selected
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-zinc-50 dark:bg-zinc-800/60 text-zinc-500 dark:text-zinc-400 font-semibold border-b border-zinc-200 dark:border-zinc-800">
              <tr>
                <th className="w-10 py-3 px-3">
                  <input type="checkbox" aria-label="Select all feedback on this page" checked={items.length > 0 && items.every((item) => selectedIds.includes(item.id))} onChange={togglePageSelection} disabled={loading || !items.length} className="h-3.5 w-3.5 accent-cyan-500" />
                </th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Priority</th>
                <th className="py-3 px-4">Category</th>
                <th className="py-3 px-4">Source</th>
                <th className="py-3 px-4">Description</th>
                <th className="py-3 px-4">Tester</th>
                <th className="py-3 px-4">Route</th>
                <th className="py-3 px-4">Submitted</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
              {loading ? (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-zinc-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-cyan-500" />
                    <span className="mt-2 block text-xs">Loading feedback reports...</span>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-zinc-500">
                    No feedback submissions match your criteria.
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const sBadge = STATUS_BADGES[item.status] || STATUS_BADGES.NEW;
                  const pBadge = PRIORITY_BADGES[item.priority] || PRIORITY_BADGES.MEDIUM;
                  return (
                    <tr
                      key={item.id}
                      className={`hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors cursor-pointer ${selectedIds.includes(item.id) ? 'bg-cyan-500/5 dark:bg-cyan-500/10' : ''}`}
                      onClick={() => handleOpenDetail(item.id)}
                    >
                      <td className="w-10 py-3 px-3" onClick={(event) => event.stopPropagation()}>
                        <input type="checkbox" aria-label={`Select feedback ${item.category.replaceAll('_', ' ')}`} checked={selectedIds.includes(item.id)} onChange={() => toggleFeedbackSelection(item.id)} className="h-3.5 w-3.5 accent-cyan-500" />
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${sBadge.bg} ${sBadge.text}`}>
                          {sBadge.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-[11px] font-medium">
                        <span className={pBadge.text} style={{ fontFamily: '"Inter Variable", Arial, sans-serif' }}>
                          {pBadge.label.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-zinc-800 dark:text-zinc-200">
                        {item.category.replace('_', ' ')}
                      </td>
                      <td className="py-3 px-4 text-zinc-500 dark:text-zinc-400">
                        {item.source === 'REGULAR_CUSTOMER' ? 'Regular Customer' : 'Beta Tester'}
                      </td>
                      <td className="py-3 px-4 text-zinc-600 dark:text-zinc-300 max-w-xs truncate font-normal">
                        {item.description}
                      </td>
                      <td className="py-3 px-4 text-zinc-500 dark:text-zinc-400">
                        {item.testerName || item.testerEmail || 'Beta Tester'}
                      </td>
                      <td className="py-3 px-4 font-mono text-[11px] text-zinc-400 truncate max-w-[120px]">
                        {item.pageRoute || '/'}
                      </td>
                      <td className="py-3 px-4 text-zinc-500 dark:text-zinc-400 whitespace-nowrap">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center justify-end gap-1">
                          {item.screenshotPath || item.screenshotUrl || (item.screenshots?.length ?? 0) > 0 ? (
                            <span
                              aria-label="Screenshot attached"
                              title="Screenshot attached"
                              className="rounded p-1 text-cyan-500"
                            >
                              <ImageIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                          <button
                            type="button"
                            aria-label="Preview feedback"
                            onClick={(e) => { e.stopPropagation(); handleOpenDetail(item.id); }}
                            className="rounded p-1 text-zinc-400 hover:text-cyan-500"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/20 text-xs">
          <span className="text-zinc-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1 rounded border border-zinc-300 dark:border-zinc-700 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1 rounded border border-zinc-300 dark:border-zinc-700 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 4. Feedback Detail Modal */}
      {selectedFeedback && (
        <div data-testid="feedback-detail-backdrop" className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setSelectedFeedback(null)}>
          <div role="dialog" aria-modal="true" aria-label="Feedback details" onClick={(event) => event.stopPropagation()} className="w-full max-w-3xl rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4 bg-zinc-50 dark:bg-zinc-800/40">
              <div>
                <span className="text-[10px] font-mono uppercase text-zinc-400 block">ID: {selectedFeedback.id}</span>
                <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2 mt-0.5">
                  <span>{selectedFeedback.category.replace('_', ' ')}</span>
                  <span className={`inline-flex items-center px-2 py-0 rounded-lg text-[10px] font-semibold border ${STATUS_BADGES[selectedFeedback.status]?.bg} ${STATUS_BADGES[selectedFeedback.status]?.text}`}>
                    {selectedFeedback.status}
                  </span>
                </h3>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {/* Triage controls stay beside the feedback title on wider screens and wrap safely on mobile. */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-zinc-500">Status:</span>
                    <select
                      value={selectedFeedback.status}
                      onChange={(e) => handleStatusChange(e.target.value as FeedbackStatus)}
                      disabled={updating}
                      className="text-xs rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-2 py-1 font-semibold text-zinc-800 dark:text-zinc-100"
                    >
                      <option value="NEW">New</option>
                      <option value="REVIEWING">Reviewing</option>
                      <option value="FIXED">Fixed</option>
                      <option value="CLOSED">Closed</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-zinc-500">Priority:</span>
                    <select
                      value={selectedFeedback.priority}
                      onChange={(e) => handlePriorityChange(e.target.value as FeedbackPriority)}
                      disabled={updating}
                      className="text-xs rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-2 py-1 font-semibold text-zinc-800 dark:text-zinc-100"
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="CRITICAL">Critical</option>
                    </select>
                  </div>
                  {updating && <Loader2 className="h-4 w-4 animate-spin text-cyan-500" />}
                </div>
                <button
                  onClick={() => setSelectedFeedback(null)}
                  aria-label="Close feedback details"
                  className="rounded-lg p-1.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Description */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Tester Description</h4>
                <div className="p-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/60 text-sm text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap">
                  {selectedFeedback.description}
                </div>
              </div>

              {/* Screenshots and their annotations are kept together for review. */}
              {selectedFeedback.screenshots?.length || selectedFeedback.screenshotUrl || selectedFeedback.screenshotPath ? (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">
                    Attached Screenshots ({selectedFeedback.screenshots?.length || 1})
                  </h4>
                  {selectedFeedback.screenshots?.length ? (
                    <div className="space-y-3">
                      {selectedFeedback.screenshots.map((screenshot, index) => {
                        const linkedAnnotations = selectedFeedback.annotations.filter((annotation) =>
                          annotation.screenshotId === screenshot.id || (!annotation.screenshotId && index === 0),
                        );
                        return (
                          <div key={screenshot.id} className="space-y-2">
                            {screenshot.screenshotUrl ? (
                              <button type="button" onClick={() => setActiveScreenshotPreview({ url: screenshot.screenshotUrl!, alt: `Feedback screenshot ${index + 1}` })} aria-label={`Enlarge feedback screenshot ${index + 1}`} className="block max-h-96 w-full overflow-hidden rounded-xl border border-zinc-200 bg-black text-left focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:border-zinc-800" style={{ borderColor: '#63D806' }}>
                                <img src={screenshot.screenshotUrl} alt={`Feedback screenshot ${index + 1}`} className="max-h-96 w-full object-contain" style={{ backgroundColor: '#5AD905', border: '1px solid #56DB07', borderRadius: '0.8px' }} />
                              </button>
                            ) : <div className="text-xs text-amber-500">Screenshot storage path exists but could not generate signed link.</div>}
                            {linkedAnnotations.length > 0 && (
                              <div className="space-y-2">
                                <h5 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Numbered Annotations ({linkedAnnotations.length})</h5>
                                {linkedAnnotations.map((ann) => (
                                  <div key={ann.index} style={{ color: '#FFFFFF', backgroundColor: '#121214', borderColor: '#777575', borderWidth: '0.2px' }} className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-3 text-xs dark:border-zinc-800 dark:bg-zinc-900">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-[10px] font-bold text-white">{ann.index}</span>
                                    <div style={{ color: '#FFFFFF' }} className="space-y-1">
                                      <code className="text-[11px]">{ann.elementSelector}</code>
                                      <p className="font-medium">{ann.comment || 'No comment added'}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : selectedFeedback.screenshotUrl ? (
                    <div className="space-y-2">
                      <button type="button" onClick={() => setActiveScreenshotPreview({ url: selectedFeedback.screenshotUrl!, alt: 'Feedback screenshot' })} aria-label="Enlarge feedback screenshot" className="block max-h-96 w-full overflow-hidden rounded-xl border border-zinc-200 bg-black text-left focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:border-zinc-800" style={{ borderColor: '#63D806' }}>
                        <img src={selectedFeedback.screenshotUrl} alt="Feedback screenshot" className="max-h-96 w-full object-contain" style={{ backgroundColor: '#5AD905', border: '1px solid #56DB07', borderRadius: '0.8px' }} />
                      </button>
                      {selectedFeedback.annotations.length > 0 && (
                        <div className="space-y-2">
                          <h5 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Numbered Annotations ({selectedFeedback.annotations.length})</h5>
                          {selectedFeedback.annotations.map((ann) => (
                            <div key={ann.index} style={{ color: '#FFFFFF', backgroundColor: '#121214', borderColor: '#777575', borderWidth: '0.2px' }} className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-3 text-xs dark:border-zinc-800 dark:bg-zinc-900">
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-[10px] font-bold text-white">{ann.index}</span>
                              <div style={{ color: '#FFFFFF' }} className="space-y-1"><code className="text-[11px]">{ann.elementSelector}</code><p className="font-medium">{ann.comment || 'No comment added'}</p></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : <div className="text-xs text-amber-500">Screenshot storage path exists but could not generate signed link.</div>}
                  {selectedFeedback.screenshots?.length && selectedFeedback.annotations.some((annotation) => !selectedFeedback.screenshots?.some((screenshot) => screenshot.id === annotation.screenshotId) && annotation.screenshotId !== null) ? (
                    <div className="mt-3 space-y-2">
                      <h5 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Other Annotations</h5>
                      {selectedFeedback.annotations.filter((annotation) => annotation.screenshotId && !selectedFeedback.screenshots?.some((screenshot) => screenshot.id === annotation.screenshotId)).map((ann) => (
                        <div key={ann.index} style={{ color: '#FFFFFF', backgroundColor: '#121214', borderColor: '#777575', borderWidth: '0.2px' }} className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-3 text-xs dark:border-zinc-800 dark:bg-zinc-900"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-[10px] font-bold text-white">{ann.index}</span><div style={{ color: '#FFFFFF' }} className="space-y-1"><code className="text-[11px]">{ann.elementSelector}</code><p className="font-medium">{ann.comment || 'No comment added'}</p></div></div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : selectedFeedback.annotations.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Numbered Annotations ({selectedFeedback.annotations.length})</h4>
                  {selectedFeedback.annotations.map((ann) => <div key={ann.index} style={{ color: '#FFFFFF', backgroundColor: '#121214', borderColor: '#777575', borderWidth: '0.2px' }} className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-3 text-xs dark:border-zinc-800 dark:bg-zinc-900"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-[10px] font-bold text-white">{ann.index}</span><div style={{ color: '#FFFFFF' }} className="space-y-1"><code className="text-[11px]">{ann.elementSelector}</code><p className="font-medium">{ann.comment || 'No comment added'}</p></div></div>)}
                </div>
              ) : null}

              {/* Technical Metadata */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="p-2.5 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40">
                  <span className="text-[10px] text-zinc-400 block">Tester</span>
                  <span className="font-semibold text-zinc-800 dark:text-zinc-200 truncate block">
                    {selectedFeedback.testerName || selectedFeedback.testerEmail || 'Unknown'}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40">
                  <span className="text-[10px] text-zinc-400 block">Route</span>
                  <span className="font-mono text-zinc-800 dark:text-zinc-200 truncate block">
                    {selectedFeedback.pageRoute || '/'}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40">
                  <span className="text-[10px] text-zinc-400 block">Viewport</span>
                  <span className="font-mono text-zinc-800 dark:text-zinc-200 block">
                    {selectedFeedback.metadata?.viewportWidth}x{selectedFeedback.metadata?.viewportHeight}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/40">
                  <span className="text-[10px] text-zinc-400 block">Submitted At</span>
                  <span className="text-zinc-800 dark:text-zinc-200 block truncate">
                    {new Date(selectedFeedback.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Internal Admin Notes */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">
                  Internal Admin Notes (Never exposed to testers)
                </h4>
                <textarea
                  rows={3}
                  value={adminNotesDraft}
                  onChange={(e) => setAdminNotesDraft(e.target.value)}
                  placeholder="Record investigation notes, root causes, or resolution details..."
                  className="w-full rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-3 text-xs text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveNotes}
                    disabled={updating}
                    className="px-4 py-1.5 rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-xs font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    Save Internal Notes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={deleteConfirmationOpen}
        onClose={() => { if (!deleting) setDeleteConfirmationOpen(false); }}
        onConfirm={() => { void confirmDeleteSelected(); }}
        title="Delete selected feedback?"
        message={`Permanently delete ${selectedIds.length} selected feedback ${selectedIds.length === 1 ? 'report' : 'reports'} and attached screenshots? This action cannot be undone.`}
        confirmText={deleting ? 'Deleting…' : 'Delete feedback'}
        confirmDisabled={deleting}
      />
      {activeScreenshotPreview && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Feedback screenshot preview" onClick={() => setActiveScreenshotPreview(null)}>
          <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-zinc-700 bg-zinc-950 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex shrink-0 items-center justify-between border-b border-zinc-800 px-4 py-3 text-sm font-semibold text-zinc-100">
              <span>Screenshot Preview</span>
              <button type="button" aria-label="Close screenshot preview" onClick={() => setActiveScreenshotPreview(null)} className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-3">
              <img src={activeScreenshotPreview.url} alt={activeScreenshotPreview.alt} className="max-h-[calc(92vh-4rem)] max-w-full object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BetaFeedbackSection;
